import { ReactNode, useMemo, useState } from 'react'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '../../../ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '../../../ui/popover'
import { Button } from '../../../ui/button'
import { Check, ChevronsUpDown } from 'lucide-react'
import { cn } from '../../../ui/lib/utils'
import { Country, countryCodes } from '../../Form/CountryCode/countryCodes'
import TooltipLabel from './TooltipLabel'
import { Label } from '../../../ui/label'

type Props = {
  value: string
  className?: string
  classNameLabel?: string
  tooltip?: ReactNode
  hideLabel?: boolean
  label?: string
  placeholder?: string
  setValue: (code: Country['code']) => void
}

export const Combobox = (props: Props) => {
  const [open, setOpen] = useState(false)
  const [value2, setValue2] = useState(props.value)
  const [query, setQuery] = useState('')
  const selected = useMemo(
    () =>
      countryCodes.find(
        (country) => country.code.toLowerCase() === value2.toLowerCase(),
      ),
    [value2],
  )

  const filteredCountries = useMemo(
    () =>
      query === ''
        ? countryCodes
        : countryCodes.filter(
            (country) =>
              country.name.toLowerCase().includes(query.toLowerCase()) ||
              country.code.toLowerCase().includes(query.toLowerCase()) ||
              country.dial_code.toLowerCase().includes(query.toLowerCase()),
          ),
    [query],
  )

  return (
    <div className="flex flex-col items-start w-full gap-1 group">
      <div className="mb-1">
        {props.tooltip === undefined ? (
          <Label htmlFor={props.label}>{props.label}</Label>
        ) : (
          <TooltipLabel label={props.label} tooltip={props.tooltip} />
        )}
      </div>

      <div className="flex flex-col items-start w-full gap-1">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className={`w-full font-normal justify-between`}
            >
              {value2 ? (
                <p className="flex flex-row">
                  {selected?.flag} {selected?.name} ({selected?.dial_code})
                </p>
              ) : (
                'Select country code...'
              )}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className={`w-[350px] p-0`}>
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Select country code..."
                value={query}
                onValueChange={setQuery}
              />
              <CommandEmpty>
                {query === '' ? 'Search for a country...' : 'No results found.'}
              </CommandEmpty>
              <CommandGroup>
                {filteredCountries.map((country) => (
                  <CommandItem
                    className="group"
                    key={country.code}
                    value={country.code}
                    onSelect={(currentValue: string) => {
                      // CAN'T DESELECT
                      setValue2(
                        currentValue === value2
                          ? ''
                          : filteredCountries.find(
                              (item) =>
                                item.code.toLocaleLowerCase() === currentValue,
                            )!.code,
                      )
                      // CAN DESELECT
                      //setValue2(currentValue === value2 ? '' : currentValue)
                      props.setValue(currentValue.toUpperCase())
                      setOpen(false)
                    }}
                  >
                    <Check
                      strokeWidth={3}
                      className={cn(
                        'mr-2 h-4 w-4 text-accent-foreground group-aria-selected:text-white',
                        value2.toLowerCase() === country.code.toLowerCase()
                          ? 'opacity-100'
                          : 'opacity-0',
                      )}
                    />
                    {country.flag} {country.name} ({country.dial_code})
                  </CommandItem>
                ))}
              </CommandGroup>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  )
}
